import { SubscriptionStatusBadge } from "@/components/payments";
import { PremiumUpsellModal } from "@/components/premium-upsell";
import { Button } from "@bidbattles/bloks/components/ui/button";
import { useUser, useUserReferralCode } from "@/hooks/user";
import { BBBackendQueryOptions } from "@bidbattles/bloks/lib/rapace";
import { usePayments } from "@/lib/backend/payments";
import {
	getSubscriptionLabel,
	hadPremiumSubscription,
	hasPremiumSubscription,
} from "@/lib/backend/utils";
import { formatCurrency } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Copy01 } from "untitledui-js/react";

export const Route = createFileRoute("/settings/dashboard")({
	async loader({ context }) {
		await context.queryClient.ensureQueryData(
			BBBackendQueryOptions.fetchReferralCode({}),
		);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const user = useUser();
	const referralCode = useUserReferralCode();
	const payments = usePayments();

	const cards = [
		{
			label: "Account",
			value: user && (
				<span className="inline-flex flex-col gap-2 items-start">
					{getSubscriptionLabel(user.subscription, { withStatusSuffix: false })}
					{hadPremiumSubscription(user.subscription) && (
						<SubscriptionStatusBadge sub={user.subscription} />
					)}
				</span>
			),
			action:
				user && !hasPremiumSubscription(user.subscription) ? (
					<PremiumUpsellModal
						payments={payments}
						trigger={
							<Button variant="link" className="relative" size="sm">
								💎{" "}
								{hadPremiumSubscription(user.subscription)
									? m.upsell__pls_come_back()
									: m.upsell__get_premium()}
							</Button>
						}
					/>
				) : null,
		},
		{
			label: "Loyalty coins",
			value: formatCurrency(
				"LOYALTY_POINTS",
				user?.loyalty_points.balance ?? 0,
			),
		},
		{
			label: "Referral code",
			value: referralCode,
			action: (
				<Button
					variant="icon"
					size="content"
					className="text-xl p-0.5 -mt-0.5 -me-0.5"
					onClick={async () => {
						const referralLink = new URL(
							`/auth/signup?referral_code=${referralCode}`,
							window?.location.origin,
						);
						await navigator.clipboard.writeText(referralLink.toString());
						toast.info("Referral link copied to clipboard! Share and earn!");
					}}
				>
					<Copy01 stroke="currentColor" size="1em" />
					<span className="sr-only">Copy to clipboard</span>
				</Button>
			),
		},
	];

	return (
		<>
			<div className="flex flex-col md:grid md:grid-cols-3 gap-4">
				{cards.map((card) => (
					<div
						key={card.label}
						className="relative p-4 border border-gray-300 rounded-lg shadow-sm shadow-gray-300/75 flex flex-col gap-4"
					>
						<div className="flex items-start justify-between">
							<p className="text-gray-500 text-sm">{card.label}</p>
							{!!card.action && card.action}
						</div>
						<p className="text-xl md:text-2xl font-medium">{card.value}</p>
					</div>
				))}
			</div>
		</>
	);
}
